import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode
} from 'react';
import { Socket } from 'socket.io-client';
import { webSocketManager } from '../../services/websocket';

interface WebSocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextValue>({
  socket: null,
  isConnected: false
});

interface WebSocketProviderProps {
  children: ReactNode;
  token: string | null;
}

/**
 * WebSocket Provider
 * Manages Socket.IO connection lifecycle based on authentication state
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  token
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (token) {
      // Connect when user is authenticated
      const socketInstance = webSocketManager.connect(token);
      setSocket(socketInstance);

      const handleConnect = () => setIsConnected(true);
      const handleDisconnect = () => setIsConnected(false);

      socketInstance.on('connect', handleConnect);
      socketInstance.on('disconnect', handleDisconnect);

      // Set initial state
      setIsConnected(socketInstance.connected);

      return () => {
        socketInstance.off('connect', handleConnect);
        socketInstance.off('disconnect', handleDisconnect);
      };
    } else {
      // Disconnect when user logs out
      webSocketManager.disconnect();
      setSocket(null);
      setIsConnected(false);
    }
  }, [token]);

  return (
    <WebSocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to access WebSocket context
 */
export const useWebSocket = (): WebSocketContextValue => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};
